import { inject, InjectionToken, NgModule, ProviderToken } from '@angular/core';
import {
  CustomerUser,
  PartnerCenterBillingConfig,
  PartnerCenterBillingCoreModule,
  Recipient,
  RecipientProvider,
} from '@galaxy/partner-center-client/billing/static/core';
import { PARTNER_ID_INJECTION_TOKEN } from '../../core/constants';
import { combineLatest, firstValueFrom, Observable, switchMap } from 'rxjs';
import { AccountGroupService, ProjectionFilter, ReadFilter } from '@galaxy/account-group';
import { map } from 'rxjs/operators';
import { BrandingV2Interface } from '@vendasta/partner';
import { BrandingV2Service, SubscriptionTiersService } from '@galaxy/partner';
import { AccountService } from '../../core/account.service';
import { MatDialog } from '@angular/material/dialog';
import { AssignedSalespersonService } from '../../assigned-salesperson/assigned-salesperson.service';
import { AssignedSalespersonDialogComponent } from '../../assigned-salesperson/assigned-salesperson-dialog/assigned-salesperson-dialog.component';
import { PartnerTrialStoreService, TrialStatus } from '../../core/partner';
import { SupportNumberService } from '../../common/help-menu';
import { PartnerService } from '../../core/partner.service';
import { RecipientProviderService } from '../../invoice/recipients/recipient-provider.service';

export const PARTNER_CENTER_TOKEN_FOR_PCC_BILLING_CONFIG = new InjectionToken<PartnerCenterBillingConfig>(
  '[partner-center-client]: token for partner-center-client/billing config',
  {
    providedIn: 'root',
    factory: () => {
      const partnerId$ = inject('PARTNER_ID' as unknown as unknown as InjectionToken<Observable<string>>);
      const marketId$ = inject('MARKET_ID' as unknown as InjectionToken<Observable<string>>);
      const accountGroupService = inject(AccountGroupService);
      const brandingService = inject(BrandingV2Service);
      const accountService = inject(AccountService);
      const assignedSalespersonService = inject(AssignedSalespersonService);
      const dialog = inject(MatDialog);

      const trialService = inject(PartnerTrialStoreService);
      const partnerService = inject(PartnerService);
      const supportNumberService = inject(SupportNumberService);

      const recipientService: RecipientProvider<Recipient> = inject(RecipientProviderService);

      return {
        merchantId$: inject(PARTNER_ID_INJECTION_TOKEN as unknown as ProviderToken<Observable<string>>),
        marketId$: inject('MARKET_ID' as unknown as InjectionToken<Observable<string>>),
        customerService: {
          getCustomer(customerId: string): Observable<{ name: string; marketId: string }> {
            return accountGroupService
              .getMulti(
                [customerId],
                new ProjectionFilter({ napData: true, accountGroupExternalIdentifiers: true }),
                new ReadFilter({ includeDeleted: true }),
              )
              .pipe(map((a) => ({ name: a[0]?.napData?.companyName, marketId: a[0]?.externalIdentifiers?.marketId })));
          },
          getUsers(customerId: string): Observable<CustomerUser[]> {
            return partnerId$.pipe(
              switchMap((partnerId) => accountService.loadUsers(customerId, partnerId)),
              map((users) =>
                users.map((u) => {
                  // Patch the user here - prefer unifiedUserId
                  return { ...u, userId: u.unifiedUserId ?? u.userId };
                }),
              ),
            );
          },
        },
        merchantService: {
          getBranding(): Observable<Pick<BrandingV2Interface, 'name' | 'logoUrl' | 'primaryColor'>> {
            return combineLatest([partnerId$, marketId$]).pipe(
              switchMap(([partnerId, marketId]) => brandingService.getBranding(partnerId, marketId)),
            );
          },
          isFreeMerchant$: partnerService.subscriptionTier$.pipe(map((s) => SubscriptionTiersService.isFree(s))),
          isTrialActive$: trialService.partnerTrial$.pipe(map((t) => t.status === TrialStatus.ACTIVE)),
          supportNumber$: supportNumberService.getSupportNumber(),
        },
        recipientProvider: recipientService,

        openAssignedSalespersonDialog: async () => {
          const config = await firstValueFrom(assignedSalespersonService.dialogConfig$);
          dialog.open(AssignedSalespersonDialogComponent, config);
        },
      };
    },
  },
);

@NgModule({
  imports: [PartnerCenterBillingCoreModule.forRoot(PARTNER_CENTER_TOKEN_FOR_PCC_BILLING_CONFIG)],
})
export class BillingProviderModule {}
